import React, { useState, useMemo, useRef, useEffect } from 'react';
import Icon from '../shared/Icon';
import Avatar from '../shared/Avatar';
import Badge from '../shared/Badge';
import LogViewerModal from '../shared/LogViewerModal';

const AVATAR_COLORS = ['#CC1A00', '#2563EB', '#7C3AED', '#15803D', '#D97706', '#0891B2', '#EC4899', '#EA580C', '#0D9488', '#6366F1'];

const TITLE_LIST_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/v1/fuso-ascent-pnc-search/title_list'
  : '/v1/fuso-ascent-pnc-search/title_list';

const CHAT_HISTORY_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/v1/fuso-ascent-pnc-search/chat_history'
  : '/v1/fuso-ascent-pnc-search/chat_history';

function relTime(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diff < 1)    return 'just now';
  if (diff < 60)   return diff + 'm ago';
  if (diff < 1440) return Math.floor(diff / 60) + 'h ago';
  const days = Math.floor(diff / 1440);
  return days === 1 ? 'Yesterday' : days + 'd ago';
}

function fmtTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const MD_TABLE_STYLES = `
  .admin-ai-resp table { border-collapse: collapse; width: 100%; font-size: 11px; margin: 10px 0; }
  .admin-ai-resp table th, .admin-ai-resp table td { border: 1px solid #E8EAED; padding: 5px 9px; text-align: left; vertical-align: top; }
  .admin-ai-resp table th { background: #F9FAFB; font-weight: 600; color: #374151; }
  .admin-ai-resp table tr:nth-child(even) td { background: #FAFAFA; }
`;

function PlainLines({ text }) {
  return (
    <span>
      {text.split('\n').map((line, li) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <span key={li}>
            {li > 0 && <br />}
            {parts.map((p, pi) =>
              p.startsWith('**') && p.endsWith('**')
                ? <strong key={pi}>{p.slice(2, -2)}</strong>
                : <span key={pi}>{p}</span>
            )}
          </span>
        );
      })}
    </span>
  );
}

function MdText({ text }) {
  if (!text) return null;
  // Split on <table>...</table> blocks so they render as real HTML
  const segments = text.split(/(<table[\s\S]*?<\/table>)/gi);
  return (
    <div className="admin-ai-resp">
      <style>{MD_TABLE_STYLES}</style>
      {segments.map((seg, i) =>
        /<table/i.test(seg)
          ? <div key={i} style={{ overflowX: 'auto' }} dangerouslySetInnerHTML={{ __html: seg }} />
          : <PlainLines key={i} text={seg} />
      )}
    </div>
  );
}

// DB stores literal \n (two-char sequence) as separator between metadata fields
function parseUserQuestion(text) {
  if (!text) return { meta: [], queryText: text || '' };
  const lines = text.split('\\n').map(l => l.trim()).filter(Boolean);

  const extract = (pattern) => {
    const m = text.match(pattern);
    return m && m[1] ? m[1].trim() : '';
  };

  const vin            = extract(/\*\*VIN\*\*:([^\n\\]+)/i);
  const mainGroup      = extract(/\*\*Main Group\*\*:([^\n\\]+)/i);
  const subGroup       = extract(/\*\*Sub group\*\*:([^\n\\]+)/i);
  const model          = extract(/\*\*Model\*\*:([^\n\\]+)/i);
  const classification = extract(/\*\*Classification\*\*:([^\n\\]+)/i);
  const chassis1       = extract(/\*\*Chassis (?:No\. )?1\*\*:([^\n\\]+)/i);
  const chassis2       = extract(/\*\*Chassis (?:No\. )?2\*\*:([^\n\\]+)/i);

  const meta = [];
  if (vin)            meta.push({ label: 'VIN',            value: vin });
  if (model)          meta.push({ label: 'Model',          value: model });
  if (classification) meta.push({ label: 'Classification', value: classification });
  if (chassis1)       meta.push({ label: 'Chassis No. 1', value: chassis1 });
  if (chassis2)       meta.push({ label: 'Chassis No. 2', value: chassis2 });
  if (mainGroup)      meta.push({ label: 'Main Group',     value: mainGroup });
  if (subGroup)       meta.push({ label: 'Sub Group',      value: subGroup });

  const queryText = lines[lines.length - 1] || text;
  return { meta, queryText };
}

function UserQuestionCard({ text }) {
  const { meta, queryText } = parseUserQuestion(text);
  return (
    <div style={{
      background: '#F0F2F5',
      borderRadius: 18,
      padding: '12px 16px',
      maxWidth: 460,
      marginLeft: 'auto',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {meta.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            Selection Summary
          </div>
          <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(204,26,0,0.08)', border: '1px solid #FFE4E4' }}>
            {meta.map((item, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '110px 1fr',
                borderBottom: i < meta.length - 1 ? '1px solid #FFF0F0' : 'none',
              }}>
                <div style={{ background: i % 2 === 0 ? '#FFF5F5' : '#FFF0F0', padding: '6px 10px', fontSize: 11, color: '#6B7280', fontWeight: 600 }}>
                  {item.label}
                </div>
                <div style={{ background: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA', padding: '6px 10px', fontSize: 11, color: '#111827', fontWeight: 500 }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, color: '#1F2937', fontWeight: 500, lineHeight: 1.55 }}>{queryText}</div>
    </div>
  );
}

const SKIP_FIELDS = new Set([
  'part_number','partNumber','part_name_code','partNameCode',
  'portal_description','description','alternative_part_numbers',
  'alternativePartNumbers','alternative_trace','alternativeTrace',
]);

function RecordItem({ rec, index }) {
  const partNum  = rec.part_number || rec.partNumber || '';
  const partCode = rec.part_name_code || rec.partNameCode || '';
  const desc     = rec.portal_description || rec.description || '';
  const altParts = rec.alternative_part_numbers || rec.alternativePartNumbers || '';
  const extras   = Object.entries(rec).filter(([k, v]) => !SKIP_FIELDS.has(k) && v && (typeof v === 'string' || typeof v === 'number'));

  // When English keys are absent (e.g. old records with Japanese field names), derive a
  // display part-number from the first short string value in the record.
  const displayNum = partNum || (
    Object.values(rec).find(v => typeof v === 'string' && v.length > 0 && v.length <= 30) || '—'
  );

  return (
    <div style={{
      background: 'white',
      borderRadius: 10,
      padding: '10px 12px',
      marginBottom: 6,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      border: '1px solid #F5F5F5',
      fontSize: 12,
      lineHeight: 1.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: partCode || desc ? 4 : 0 }}>
        <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, minWidth: 20 }}>{index + 1}.</span>
        <span style={{
          fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#CC1A00',
          background: '#FFF5F5', padding: '2px 8px', borderRadius: 6, border: '1px solid #FEE2E2',
        }}>{displayNum}</span>
        {partCode && (
          <span style={{ fontSize: 11, color: '#6B7280', background: '#F9FAFB', padding: '2px 7px', borderRadius: 6, border: '1px solid #F3F4F6' }}>
            {partCode}
          </span>
        )}
      </div>
      {extras.map(([k, v]) => (
        <div key={k} style={{ color: '#6B7280', marginTop: 2, fontSize: 11 }}>
          <strong style={{ color: '#9CA3AF' }}>{k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}:</strong> {v}
        </div>
      ))}
      {altParts && (
        <div style={{ color: '#9CA3AF', marginTop: 3, fontSize: 11 }}>
          <strong>Alt:</strong> {altParts}
        </div>
      )}
      {desc && (
        <div style={{ color: '#4B5563', marginTop: 4, fontSize: 12, borderTop: '1px solid #F9FAFB', paddingTop: 4 }}>
          {desc}
        </div>
      )}
    </div>
  );
}

// Try multiple strategies to parse a potentially messy JSON blob from old DB records.
// Old records may have control characters in values or extra surrounding text.
function tryParseJson(text) {
  if (!text || typeof text !== 'string') return null;
  // Strategy 1: direct parse
  try { return JSON.parse(text); } catch (_) {}
  // Strategy 2: strip control characters then parse
  try {
    const sanitized = text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ' ');
    return JSON.parse(sanitized);
  } catch (_) {}
  // Strategy 3: extract the outermost {...} block and parse that
  try {
    const m = text.match(/(\{[\s\S]*\})/);
    if (m) {
      const sanitized = m[1].replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ' ');
      return JSON.parse(sanitized);
    }
  } catch (_) {}
  return null;
}

function AiResponseText({ text, images, excelUrl }) {
  if (!text) return null;

  const parsed = tryParseJson(text);

  // Treat any response that has a records array as a PNC search result
  const isSuccess = parsed && Array.isArray(parsed.records) && parsed.records.length > 0;
  const isFailure = parsed && (parsed.status === false || parsed.status === 'failure' || parsed.status === 'error');

  if (isSuccess) {
    const records = parsed.records;
    const total   = parsed.total_records || records.length;
    return (
      <div>
        {/* Status row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{
            background: '#DCFCE7', color: '#15803D', fontSize: 11, fontWeight: 700,
            padding: '3px 12px', borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {total} part{total !== 1 ? 's' : ''} found
          </span>
          {parsed.intent && (
            <span style={{
              background: '#DBEAFE', color: '#1D4ED8', fontSize: 11, fontWeight: 700,
              padding: '3px 12px', borderRadius: 20,
            }}>
              {parsed.intent}
            </span>
          )}
        </div>

        {/* Records in scrollable container */}
        <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 2 }}>
          {records.map((rec, i) => <RecordItem key={i} rec={rec} index={i} />)}
        </div>

        {/* Images */}
        {images && images.length > 0 && (
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {images.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer" style={{
                display: 'block', borderRadius: 12, overflow: 'hidden',
                border: '1px solid #E8EAED', maxHeight: 180,
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
              }}>
                <img src={url} alt={`Part diagram ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </a>
            ))}
          </div>
        )}

        {/* Excel download */}
        {excelUrl && (
          <div style={{ marginTop: 10 }}>
            <a href={excelUrl} target="_blank" rel="noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#F0FDF4', color: '#15803D',
              border: '1px solid #BBF7D0', borderRadius: 20,
              padding: '5px 14px', fontSize: 12, fontWeight: 600, textDecoration: 'none',
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download Excel
            </a>
          </div>
        )}
      </div>
    );
  }

  if (isFailure) {
    return (
      <span style={{ color: '#DC2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        No results found
      </span>
    );
  }

  // Plain-text (markdown + HTML tables) response — still render images and excel if present
  return (
    <div>
      <MdText text={text} />
      {images && images.length > 0 && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {images.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" style={{
              display: 'block', borderRadius: 12, overflow: 'hidden',
              border: '1px solid #E8EAED', maxHeight: 180,
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            }}>
              <img src={url} alt={`Part diagram ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </a>
          ))}
        </div>
      )}
      {excelUrl && (
        <div style={{ marginTop: 10 }}>
          <a href={excelUrl} target="_blank" rel="noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#F0FDF4', color: '#15803D',
            border: '1px solid #BBF7D0', borderRadius: 20,
            padding: '5px 14px', fontSize: 12, fontWeight: 600, textDecoration: 'none',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download Excel
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Left pane: user list ─────────────────────────────────────────────────────

function UserPane({ users, devIds, selectedId, onSelect, excludeDevs, accent }) {
  const ac = accent || '#CC1A00';
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users
      .filter(u => excludeDevs ? !devIds.includes(u.user_id) : true)
      .filter(u => !q || u.email.toLowerCase().includes(q) || u.user_id.toLowerCase().includes(q))
      .sort((a, b) => b.query_count - a.query_count);
  }, [users, devIds, search, excludeDevs]);

  return (
    <div style={{ width: 272, borderRight: '1px solid #E8EAED', display: 'flex', flexDirection: 'column', flexShrink: 0, background: 'white' }}>
      <div style={{ padding: '14px 12px 10px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
          Users <span style={{ fontWeight: 500, color: '#D1D5DB' }}>({filtered.length})</span>
        </div>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex' }}>
            <Icon name="search" size={13} color="#9CA3AF" />
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search users..."
            style={{
              width: '100%', padding: '8px 10px 8px 30px',
              border: '1px solid #E8EAED', borderRadius: 10,
              fontSize: 12, outline: 'none', fontFamily: 'inherit',
              color: '#111827', boxSizing: 'border-box', background: '#F9FAFB',
              transition: 'border-color 0.15s',
            }}
          />
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {filtered.map((u, i) => {
          const active = u.user_id === selectedId;
          const aColor = AVATAR_COLORS[i % AVATAR_COLORS.length];
          const is_dev = devIds.includes(u.user_id);
          return (
            <button
              key={u.user_id}
              onClick={() => onSelect(u)}
              style={{
                width: '100%', padding: '10px 12px', border: 'none',
                background: active ? '#FFF5F5' : 'transparent',
                borderLeft: '3px solid ' + (active ? ac : 'transparent'),
                cursor: 'pointer', textAlign: 'left',
                display: 'flex', gap: 10, alignItems: 'center',
                fontFamily: 'inherit',
                transition: 'background 0.1s',
              }}
            >
              <Avatar name={u.email} size={34} bg={aColor} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 145 }}>
                    {u.email}
                  </span>
                  {is_dev && <Badge text="DEV" color="#7C3AED" bg="#F5F3FF" size={10} />}
                </div>
                <div style={{ fontSize: 11, color: active ? ac : '#9CA3AF', fontWeight: 600 }}>
                  {u.query_count.toLocaleString()} queries
                </div>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
            No users match your search
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Middle pane: session list ────────────────────────────────────────────────

function SessionRow({ session, active, onSelect, accent }) {
  const ac = accent || '#CC1A00';
  return (
    <button
      onClick={() => onSelect(session.chat_id)}
      style={{
        width: '100%', padding: '10px 14px', border: 'none',
        background: active ? '#FFF5F5' : 'transparent',
        borderLeft: '3px solid ' + (active ? ac : 'transparent'),
        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
        transition: 'background 0.1s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        {session.pinned === 1 && <span style={{ color: '#D97706', fontSize: 11, marginTop: 2, flexShrink: 0 }}>📌</span>}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {session.title}
          </div>
          <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4, fontWeight: 500 }}>
            {relTime(session.time_stamp)}
          </div>
        </div>
      </div>
    </button>
  );
}

function SessionPane({ sessions, sessionsLoading, selectedUser, selectedSessionId, onSelect, accent }) {
  const ac = accent || '#CC1A00';

  if (!selectedUser) {
    return (
      <div style={{ width: 272, borderRight: '1px solid #E8EAED', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAFA', flexShrink: 0 }}>
        <div style={{ textAlign: 'center', color: '#D1D5DB', padding: 24 }}>
          <Icon name="users" size={28} color="#E5E7EB" />
          <div style={{ marginTop: 8, fontSize: 12, color: '#9CA3AF' }}>Select a user to view sessions</div>
        </div>
      </div>
    );
  }

  const list   = sessions || [];
  const pinned = list.filter(s => s.pinned === 1);
  const rest   = list.filter(s => s.pinned !== 1);

  return (
    <div style={{ width: 272, borderRight: '1px solid #E8EAED', display: 'flex', flexDirection: 'column', flexShrink: 0, background: 'white' }}>
      <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Sessions {!sessionsLoading && <span style={{ fontWeight: 500, color: '#D1D5DB' }}>({list.length})</span>}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {sessionsLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading…</div>
        ) : list.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No sessions found</div>
        ) : (
          <>
            {pinned.length > 0 && (
              <div>
                <div style={{ fontSize: 10, color: '#D1D5DB', padding: '10px 14px 4px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Pinned</div>
                {pinned.map(s => <SessionRow key={s.chat_id} session={s} active={s.chat_id === selectedSessionId} onSelect={onSelect} accent={ac} />)}
                <div style={{ height: 1, background: '#F3F4F6', margin: '4px 14px' }} />
              </div>
            )}
            {rest.map(s => <SessionRow key={s.chat_id} session={s} active={s.chat_id === selectedSessionId} onSelect={onSelect} accent={ac} />)}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Right pane: message thread ───────────────────────────────────────────────

function FusoAvatar() {
  return (
    <div style={{
      width: 28, height: 28, flexShrink: 0, marginTop: 16,
      background: 'linear-gradient(135deg, #CC1A00 0%, #E53E1A 100%)',
      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 2px 6px rgba(204,26,0,0.28)',
    }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    </div>
  );
}

function MessagePane({ messages, messagesLoading, sessionId, sessions }) {
  const endRef = useRef(null);
  const [logModal, setLogModal] = useState({ open: false, chatRecordId: null });

  useEffect(() => {
    if (endRef.current) {
      endRef.current.parentNode.scrollTop = endRef.current.parentNode.scrollHeight;
    }
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FB' }}>
        <div style={{ textAlign: 'center', color: '#D1D5DB', padding: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <Icon name="chat" size={26} color="#D1D5DB" />
          </div>
          <div style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500 }}>Select a session to view the conversation</div>
        </div>
      </div>
    );
  }

  const turns       = messages || [];
  const sessionInfo = (sessions || []).find(s => s.chat_id === sessionId);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F8F9FB', overflow: 'hidden', minWidth: 0 }}>
      {/* Session header */}
      {sessionInfo && (
        <div style={{
          background: 'white',
          borderBottom: '1px solid #EDEEF0',
          padding: '12px 20px',
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#CC1A00', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {sessionInfo.title}
            </div>
            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1, fontWeight: 500 }}>
              {fmtTime(sessionInfo.time_stamp)}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        {messagesLoading ? (
          <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '48px' }}>Loading messages…</div>
        ) : turns.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '48px' }}>No messages found</div>
        ) : turns.map((turn, i) => (
          <div key={turn.chat_record_id || i}>
            {/* User question — right-aligned */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#C4C9D4', fontWeight: 500 }}>{fmtTime(turn.time_stamp)}</div>
              <UserQuestionCard text={turn.user_question} />
            </div>

            {/* AI response — left-aligned */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <FusoAvatar />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  FUSO Assistant
                </div>
                <div style={{
                  background: 'white',
                  border: '1px solid #FCE4E4',
                  borderRadius: '4px 18px 18px 18px',
                  padding: '14px 16px',
                  fontSize: 12,
                  color: '#374151',
                  lineHeight: 1.7,
                  boxShadow: '0 2px 8px rgba(204,26,0,0.06)',
                }}>
                  <AiResponseText text={turn.ai_response} images={turn.images} excelUrl={turn.excel_url} />
                </div>

                {/* Feedback indicator */}
                {(turn.user_feedback === 1 || turn.user_feedback === -1) && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 7, paddingLeft: 2 }}>
                    {turn.user_feedback === 1 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#F0FDF4', color: '#15803D', fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20, border: '1px solid #BBF7D0' }}>
                        <Icon name="thumbUp" size={10} color="#15803D" /> Helpful
                      </span>
                    )}
                    {turn.user_feedback === -1 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEF2F2', color: '#DC2626', fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20, border: '1px solid #FECACA' }}>
                        <Icon name="thumbDown" size={10} color="#DC2626" /> Not helpful
                      </span>
                    )}
                  </div>
                )}

                {/* View log */}
                <div style={{ marginTop: 7, paddingLeft: 2 }}>
                  <button
                    onClick={() => setLogModal({ open: true, chatRecordId: turn.chat_record_id })}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: '#F9FAFB', color: '#6B7280', fontSize: 10, fontWeight: 700,
                      padding: '2px 9px', borderRadius: 20, border: '1px solid #E8EAED',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="log" size={10} color="#6B7280" /> View log
                  </button>
                </div>
              </div>
            </div>

            {/* Turn separator */}
            {i < turns.length - 1 && (
              <div style={{ height: 1, background: 'linear-gradient(to right, transparent, #E5E7EB, transparent)', margin: '18px 0 0' }} />
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <LogViewerModal
        isOpen={logModal.open}
        chatRecordId={logModal.chatRecordId}
        onClose={() => setLogModal({ open: false, chatRecordId: null })}
      />
    </div>
  );
}

// ─── Page root ────────────────────────────────────────────────────────────────

export default function UsersPage({ users, devIds, excludeDevs, accent }) {
  const [selectedUser,      setSelectedUser]      = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessions,          setSessions]          = useState({});
  const [sessionsLoading,   setSessionsLoading]   = useState(false);
  const [messages,          setMessages]          = useState({});
  const [messagesLoading,   setMessagesLoading]   = useState(false);

  function handleUserSelect(user) {
    setSelectedUser(user);
    setSelectedSessionId(null);
    const uid = user.user_id;
    if (sessions[uid] !== undefined) return;
    setSessionsLoading(true);
    fetch(TITLE_LIST_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: uid }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d || d.status === false) {
          setSessions(prev => ({ ...prev, [uid]: [] }));
        } else {
          const list = Object.values(d).filter(item => item && item.chat_id);
          list.sort((a, b) => new Date(b.time_stamp) - new Date(a.time_stamp));
          setSessions(prev => ({ ...prev, [uid]: list }));
        }
        setSessionsLoading(false);
      })
      .catch(() => {
        setSessions(prev => ({ ...prev, [uid]: [] }));
        setSessionsLoading(false);
      });
  }

  function handleSessionSelect(chatId) {
    setSelectedSessionId(chatId);
    if (messages[chatId] !== undefined) return;
    setMessagesLoading(true);
    fetch(CHAT_HISTORY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chat_id: chatId }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d || d.error) {
          setMessages(prev => ({ ...prev, [chatId]: [] }));
        } else {
          const list = Object.values(d).filter(item => item && item.user_question);
          list.sort((a, b) => new Date(a.time_stamp) - new Date(b.time_stamp));
          setMessages(prev => ({ ...prev, [chatId]: list }));
        }
        setMessagesLoading(false);
      })
      .catch(() => {
        setMessages(prev => ({ ...prev, [chatId]: [] }));
        setMessagesLoading(false);
      });
  }

  const currentSessions = selectedUser ? sessions[selectedUser.user_id] : undefined;

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      <UserPane
        users={users}
        devIds={devIds}
        selectedId={selectedUser?.user_id}
        onSelect={handleUserSelect}
        excludeDevs={excludeDevs}
        accent={accent}
      />
      <SessionPane
        sessions={currentSessions}
        sessionsLoading={sessionsLoading}
        selectedUser={selectedUser}
        selectedSessionId={selectedSessionId}
        onSelect={handleSessionSelect}
        accent={accent}
      />
      <MessagePane
        messages={selectedSessionId ? messages[selectedSessionId] : undefined}
        messagesLoading={messagesLoading}
        sessionId={selectedSessionId}
        sessions={currentSessions}
      />
    </div>
  );
}
